/**
 * Geolocalização no login:
 *  1) GPS preciso (navigator.geolocation) + reverse-geocode Nominatim — quando o usuário permite.
 *  2) Fallback por IP: serviços de geo-IP (sem chave) enxergam o IP PÚBLICO REAL do navegador e
 *     devolvem cidade/UF/aproximado — usado quando o GPS está desligado/negado.
 * Sempre tenta capturar o IP público real (mesmo com GPS), para gravar no log.
 * Retorna {} se nada disponível (o login segue normal).
 */
const ESTADOS_BR = {
  "Acre": "AC", "Alagoas": "AL", "Amapá": "AP", "Amazonas": "AM", "Bahia": "BA",
  "Ceará": "CE", "Distrito Federal": "DF", "Espírito Santo": "ES", "Goiás": "GO",
  "Maranhão": "MA", "Mato Grosso": "MT", "Mato Grosso do Sul": "MS", "Minas Gerais": "MG",
  "Pará": "PA", "Paraíba": "PB", "Paraná": "PR", "Pernambuco": "PE", "Piauí": "PI",
  "Rio de Janeiro": "RJ", "Rio Grande do Norte": "RN", "Rio Grande do Sul": "RS",
  "Rondônia": "RO", "Roraima": "RR", "Santa Catarina": "SC", "São Paulo": "SP",
  "Sergipe": "SE", "Tocantins": "TO",
};

function ufDe(nomeOuSigla) {
  const s = (nomeOuSigla || "").trim();
  if (!s) return "";
  if (ESTADOS_BR[s]) return ESTADOS_BR[s];
  if (s.length === 2) return s.toUpperCase();
  return s;
}

function obterCoordenadas() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const ok = (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
    navigator.geolocation.getCurrentPosition(ok, () => resolve(null), {
      enableHighAccuracy: true, timeout: 8000, maximumAge: 30000,
    });
  });
}

async function geocodificarReverso(latitude, longitude) {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
      { headers: { "Accept-Language": "pt-BR" } }
    );
    if (r.ok) {
      const a = (await r.json()).address || {};
      const rua = a.road || a.pedestrian || a.street || "";
      return {
        endereco: rua + (a.house_number ? `, ${a.house_number}` : ""),  // rua + número, se houver
        bairro: a.suburb || a.neighbourhood || a.district || "",
        cidade: a.city || a.town || a.municipality || a.village || "",
        estado: ufDe(a.state),
      };
    }
  } catch { /* ignora */ }
  return {};
}

// Obtém o IPv4 público (endpoints só-IPv4) — o IP "normal" que o usuário espera ver.
// Sem isto, os serviços de geo-IP devolvem o IPv6 da conexão (celular/rede usam IPv6).
async function obterIPv4() {
  const fontes = [
    async () => (await (await fetch("https://api.ipify.org?format=json")).json()).ip,
    async () => (await (await fetch("https://ipv4.icanhazip.com")).text()).trim(),
    async () => (await (await fetch("https://api4.my-ip.io/v2/ip.json")).json()).ip,
  ];
  for (const f of fontes) {
    try {
      const ip = await f();
      if (ip && ip.includes(".") && !ip.includes(":")) return ip;  // só IPv4
    } catch { /* próximo */ }
  }
  return null;
}

// Geo por IP (sem chave): pega o IPv4 e geolocaliza ESSE IP (cidade/UF/aprox).
// Cascata de provedores; cai p/ o próximo se um falhar/rate-limit.
async function localizacaoPorIP() {
  const ipv4 = await obterIPv4();
  const provedores = [
    async () => {
      const d = await (await fetch(`https://ipapi.co/${ipv4 ? ipv4 + "/" : ""}json/`)).json();
      if (d && d.ip && !d.error) return {
        ip: ipv4 || d.ip, cidade: d.city || "", estado: ufDe(d.region_code || d.region),
        latitude: d.latitude, longitude: d.longitude,
      };
      return null;
    },
    async () => {
      const d = await (await fetch(`https://ipwho.is/${ipv4 || ""}`)).json();
      if (d && d.success !== false && d.ip) return {
        ip: ipv4 || d.ip, cidade: d.city || "", estado: ufDe(d.region),
        latitude: d.latitude, longitude: d.longitude,
      };
      return null;
    },
    async () => {
      const d = await (await fetch(`https://get.geojs.io/v1/ip/geo/${ipv4 || ""}.json`)).json();
      const r = Array.isArray(d) ? d[0] : d;
      if (r && r.ip) return {
        ip: ipv4 || r.ip, cidade: r.city || "", estado: ufDe(r.region),
        latitude: r.latitude != null ? parseFloat(r.latitude) : null,
        longitude: r.longitude != null ? parseFloat(r.longitude) : null,
      };
      return null;
    },
  ];
  for (const p of provedores) {
    try { const res = await p(); if (res && res.ip) return res; } catch { /* próximo */ }
  }
  return ipv4 ? { ip: ipv4 } : {};  // pelo menos o IPv4, se a geo falhar
}

/**
 * Captura completa: IP público real (sempre) + GPS preciso se permitido, senão localização por IP.
 * Retorna {latitude, longitude, endereco, bairro, cidade, estado, ip, fonte}.
 */
export async function capturarLocalizacao() {
  // dispara as duas em paralelo (IP real + GPS)
  const [porIP, coords] = await Promise.all([localizacaoPorIP(), obterCoordenadas()]);

  if (coords) {
    // GPS preciso → endereço fino pelo Nominatim; mantém o IP real do geo-IP
    const addr = await geocodificarReverso(coords.latitude, coords.longitude);
    return {
      latitude: coords.latitude,
      longitude: coords.longitude,
      endereco: addr.endereco || "",
      bairro: addr.bairro || "",
      cidade: addr.cidade || porIP.cidade || "",
      estado: addr.estado || porIP.estado || "",
      ip: porIP.ip || undefined,
      fonte: "gps",
    };
  }

  // sem GPS → usa a localização aproximada por IP
  if (porIP.ip || porIP.cidade) {
    return {
      latitude: porIP.latitude ?? undefined,
      longitude: porIP.longitude ?? undefined,
      endereco: "",
      bairro: "",
      cidade: porIP.cidade || "",
      estado: porIP.estado || "",
      ip: porIP.ip || undefined,
      fonte: "ip",
    };
  }
  return {};
}
